/**
 * Application routes
 */

import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { OnboardingGuard } from '../components/auth/OnboardingGuard';
import { ScrollToTop } from '../components/ScrollToTop';
import { LoginPage } from '../pages/Login';
import { RegisterPage } from '../pages/Register';
import { ForgotPasswordPage } from '../pages/ForgotPassword';
import { DashboardPage } from '../pages/Dashboard';
import { MeetingsPage } from '../pages/Meetings';
import { MeetingDetailPage } from '../pages/MeetingDetail';
import { ProfilePage } from '../pages/Profile';
import { SettingsPage } from '../pages/Settings';
import { WelcomePage } from '../pages/Onboarding/Welcome';
import { ProfileSetupPage } from '../pages/Onboarding/ProfileSetup';
import { FirstMeetingPage } from '../pages/Onboarding/FirstMeeting';
import { FeaturesOverviewPage } from '../pages/Onboarding/FeaturesOverview';
import { CompletionPage } from '../pages/Onboarding/Completion';
import { HomePage } from '../pages/landing/Home';
import { PricingPage } from '../pages/landing/Pricing';
import { EnterprisePage } from '../pages/landing/Enterprise';
import { BlogPage } from '../pages/landing/Blog';
import { BlogPostPage } from '../pages/landing/BlogPost';
import { IntegrationsPage } from '../pages/landing/IntegrationsPage';
import { SecurityPage } from '../pages/landing/SecurityPage';
import { AboutPage } from '../pages/landing/About';
import { ContactPage } from '../pages/landing/Contact';
import { CareersPage } from '../pages/landing/Careers';
import { HelpCenterPage } from '../pages/landing/HelpCenter';
import { APIPage } from '../pages/landing/API';
import { PrivacyPage } from '../pages/landing/Privacy';
import { TermsPage } from '../pages/landing/Terms';
import { ResourcesPage } from '../pages/landing/Resources';
import { DocsLayout } from '../components/docs/DocsLayout';
import { DocsHome } from '../pages/docs/DocsHome';
import { Quickstart } from '../pages/docs/Quickstart';
import { Concepts } from '../pages/docs/Concepts';
import { Models } from '../pages/docs/Models';
import { Transcription } from '../pages/docs/Features/Transcription';
import { Summaries } from '../pages/docs/Features/Summaries';
import { SpeakerDetection } from '../pages/docs/Features/SpeakerDetection';
import { Analytics } from '../pages/docs/Features/Analytics';
import { ZoomIntegration } from '../pages/docs/Integrations/Zoom';
import { GoogleMeetIntegration } from '../pages/docs/Integrations/GoogleMeet';
import { TeamsIntegration } from '../pages/docs/Integrations/Teams';
import { SlackIntegration } from '../pages/docs/Integrations/Slack';
import { APIOverview } from '../pages/docs/API/Overview';
import { APIAuthentication } from '../pages/docs/API/Authentication';
import { APIEndpoints } from '../pages/docs/API/Endpoints';
import { ConfigurationSettings } from '../pages/docs/Configuration/Settings';
import { StudyModePage } from '../pages/study/StudyMode';
import { StudyModePricingPage } from '../pages/study/StudyPricing';
import { StudyModeAppPage } from '../pages/study/StudyModeApp';
import { StudyDocumentsPage } from '../pages/study/StudyDocuments';
import { StudyDocumentDetailPage } from '../pages/study/StudyDocumentDetail';
import { StudyChatPage } from '../pages/study/StudyChat';
import { StudyFlashcardsPage } from '../pages/study/StudyFlashcards';
import { StudyAnalyticsPage } from '../pages/study/StudyAnalytics';
import { ROUTES } from '../config/constants';

// Root layout component that includes ScrollToTop
function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
}

// Error element for route-level errors
function RouteErrorElement() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Page Error
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Something went wrong loading this page. Please try again.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Reload Page
          </button>
          <div>
            <button
              onClick={() => window.history.back()}
              className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorElement />,
    children: [
      // Landing pages (public)
      {
        path: ROUTES.HOME,
        element: <HomePage />,
      },
      {
        path: ROUTES.PRICING,
        element: <PricingPage />,
      },
      {
        path: ROUTES.ENTERPRISE,
        element: <EnterprisePage />,
      },
      {
        path: ROUTES.BLOG,
        element: <BlogPage />,
      },
      {
        path: `${ROUTES.BLOG}/:slug`,
        element: <BlogPostPage />,
      },
      {
        path: ROUTES.INTEGRATIONS,
        element: <IntegrationsPage />,
      },
      {
        path: ROUTES.SECURITY,
        element: <SecurityPage />,
      },
      {
        path: ROUTES.ABOUT,
        element: <AboutPage />,
      },
      {
        path: ROUTES.CONTACT,
        element: <ContactPage />,
      },
      {
        path: ROUTES.CAREERS,
        element: <CareersPage />,
      },
      {
        path: ROUTES.HELP_CENTER,
        element: <HelpCenterPage />,
      },
      {
        path: ROUTES.API,
        element: <APIPage />,
      },
      {
        path: ROUTES.PRIVACY,
        element: <PrivacyPage />,
      },
      {
        path: ROUTES.TERMS,
        element: <TermsPage />,
      },
      {
        path: ROUTES.RESOURCES,
        element: <ResourcesPage />,
      },

      // Study Mode Landing (public)
      {
        path: ROUTES.STUDY_MODE,
        element: <StudyModePage />,
      },
      {
        path: ROUTES.STUDY_MODE_PRICING,
        element: <StudyModePricingPage />,
      },

      // Docs pages (public)
      {
        path: '/docs',
        element: (
          <DocsLayout>
            <DocsHome />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/quickstart',
        element: (
          <DocsLayout>
            <Quickstart />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/concepts',
        element: (
          <DocsLayout>
            <Concepts />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/models',
        element: (
          <DocsLayout>
            <Models />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/features/transcription',
        element: (
          <DocsLayout>
            <Transcription />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/features/summaries',
        element: (
          <DocsLayout>
            <Summaries />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/features/speaker-detection',
        element: (
          <DocsLayout>
            <SpeakerDetection />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/features/analytics',
        element: (
          <DocsLayout>
            <Analytics />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/integrations/zoom',
        element: (
          <DocsLayout>
            <ZoomIntegration />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/integrations/google-meet',
        element: (
          <DocsLayout>
            <GoogleMeetIntegration />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/integrations/teams',
        element: (
          <DocsLayout>
            <TeamsIntegration />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/integrations/slack',
        element: (
          <DocsLayout>
            <SlackIntegration />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/api/overview',
        element: (
          <DocsLayout>
            <APIOverview />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/api/authentication',
        element: (
          <DocsLayout>
            <APIAuthentication />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/api/endpoints',
        element: (
          <DocsLayout>
            <APIEndpoints />
          </DocsLayout>
        ),
      },
      {
        path: '/docs/configuration/settings',
        element: (
          <DocsLayout>
            <ConfigurationSettings />
          </DocsLayout>
        ),
      },

      // Auth pages (public)
      {
        path: ROUTES.SIGN_IN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.SIGN_UP,
        element: <RegisterPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },

      // Onboarding pages (protected, no layout)
      {
        path: ROUTES.ONBOARDING_WELCOME,
        element: (
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ONBOARDING_PROFILE,
        element: (
          <ProtectedRoute>
            <ProfileSetupPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ONBOARDING_FIRST_MEETING,
        element: (
          <ProtectedRoute>
            <FirstMeetingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ONBOARDING_FEATURES,
        element: (
          <ProtectedRoute>
            <FeaturesOverviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ONBOARDING_COMPLETE,
        element: (
          <ProtectedRoute>
            <CompletionPage />
          </ProtectedRoute>
        ),
      },

      // App pages (protected)
      {
        element: <Layout />,
        children: [
          {
            path: ROUTES.APP,
            element: (
              <ProtectedRoute>
                <OnboardingGuard>
                  <DashboardPage />
                </OnboardingGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTES.APP_MEETINGS,
            element: (
              <ProtectedRoute>
                <OnboardingGuard>
                  <MeetingsPage />
                </OnboardingGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: `${ROUTES.APP_MEETINGS}/:id`,
            element: (
              <ProtectedRoute>
                <OnboardingGuard>
                  <MeetingDetailPage />
                </OnboardingGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTES.APP_PROFILE,
            element: (
              <ProtectedRoute>
                <OnboardingGuard>
                  <ProfilePage />
                </OnboardingGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTES.APP_SETTINGS,
            element: (
              <ProtectedRoute>
                <OnboardingGuard>
                  <SettingsPage />
                </OnboardingGuard>
              </ProtectedRoute>
            ),
          },
          // Legacy routes (redirect to /app)
          {
            path: ROUTES.MEETINGS,
            element: <Navigate to={ROUTES.APP_MEETINGS} replace />,
          },
          {
            path: `${ROUTES.MEETINGS}/:id`,
            element: <Navigate to={ROUTES.APP_MEETINGS} replace />,
          },
          {
            path: ROUTES.PROFILE,
            element: <Navigate to={ROUTES.APP_PROFILE} replace />,
          },
          {
            path: ROUTES.SETTINGS,
            element: <Navigate to={ROUTES.APP_SETTINGS} replace />,
          },
        ],
      },

      // Study Mode App (Protected)
      {
        path: ROUTES.STUDY_APP,
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyModeAppPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDY_DOCUMENTS,
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyDocumentsPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/study/app/documents/:id',
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyDocumentDetailPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDY_CHAT,
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyChatPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDY_FLASHCARDS,
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyFlashcardsPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDY_ANALYTICS,
        element: (
          <ProtectedRoute>
            <OnboardingGuard>
              <StudyAnalyticsPage />
            </OnboardingGuard>
          </ProtectedRoute>
        ),
      },

      {
        path: '*',
        element: <Navigate to={ROUTES.HOME} replace />,
      },
    ],
  },
]);

